import { useState, useEffect } from 'react';

interface WaitTimeData {
  BekkancurrentNumber: number;
  BekkancurrentWaiting: number;
  ShisuicurrentNumber: number;
  ShisuicurrentWaiting: number;
  YataicurrentNumber: number;
}

export default function Home() {
  const [currentTime, setCurrentTime] = useState(new Date());
  const [waitTimeData, setWaitTimeData] = useState<WaitTimeData | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentTime(new Date());
    }, 60000);
    return () => clearInterval(timer);
  }, []);

  useEffect(() => {
    const fetchWaitTime = async () => {
      try {
        const response = await fetch('https://yatai-yobidashi-default-rtdb.firebaseio.com/.json');
        const data = await response.json();
        setWaitTimeData(data);
        setIsLoading(false);
      } catch (error) {
        console.error('待ち時間データの取得に失敗しました:', error);
        setIsLoading(false);
      }
    };

    fetchWaitTime();
    const interval = setInterval(fetchWaitTime, 60000);
    return () => clearInterval(interval);
  }, []);

  const scrollToSection = (id: string) => {
    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <div className="min-h-screen bg-white">
      {/* ヘッダー */}
      <header className="fixed top-0 left-0 right-0 z-50 transition-all duration-300 bg-white/95 backdrop-blur-sm shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-20">
            <div className="flex items-center">
              <div className="w-32 h-16 flex items-center justify-center">
                <img 
                  src="https://static.readdy.ai/image/a73d7a96b404061874ea59f844e08d9e/2817b4433ac19bc7f00b3fd73fdf23ba.png" 
                  alt="川豊グループ" 
                  className="w-full h-full object-contain"
                />
              </div>
            </div>
            <nav className="hidden md:flex items-center space-x-8">
              <button onClick={() => scrollToSection('comparison')} className="text-sm font-medium text-gray-700 hover:text-red-600 transition-colors whitespace-nowrap cursor-pointer">店舗比較</button>
              <button onClick={() => scrollToSection('wait-time')} className="text-sm font-medium text-gray-700 hover:text-red-600 transition-colors whitespace-nowrap cursor-pointer">待ち時間</button>
              <button onClick={() => scrollToSection('stores')} className="text-sm font-medium text-gray-700 hover:text-red-600 transition-colors whitespace-nowrap cursor-pointer">店舗詳細</button>
              <a href="https://sywgckeu.gensparkspace.com/" target="_blank" rel="noopener noreferrer" className="text-sm font-medium text-gray-700 hover:text-red-600 transition-colors whitespace-nowrap cursor-pointer flex items-center">
                <i className="ri-gamepad-line mr-1"></i>
                ゲーム
              </a>
            </nav>
          </div>
        </div>
      </header>

      {/* ヒーローセクション */}
      <section className="relative pt-20 bg-gradient-to-br from-red-50 via-orange-50 to-amber-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 text-center">
          <h1 className="text-3xl md:text-5xl font-bold text-gray-900 mb-4 leading-tight">
            川豊グループ<br className="md:hidden" />
            店舗案内・待ち時間のご案内
          </h1>
          <p className="text-lg md:text-xl text-red-600 font-semibold mb-6">
            成田でうなぎを食べるならこちら<br />
            店舗ごとの営業情報・待ち時間がすぐ分かります
          </p>
          <div className="max-w-3xl mx-auto bg-white rounded-2xl shadow-lg p-6 md:p-8 text-left">
            <p className="text-base md:text-lg text-gray-700 leading-relaxed mb-4">
              川豊グループは、成田市内に複数の店舗がございます。<br />
              店舗ごとに営業形態や待ち時間の確認方法が異なりますので、<br />
              ご来店前に下記の情報をご確認ください。
            </p>
            <p className="text-base md:text-lg text-gray-700 leading-relaxed">
              現在の待ち時間やご案内状況も、こちらのページでご確認いただけます。
            </p>
          </div>
          
          {/* クイックナビゲーション */}
          <div className="mt-12 max-w-5xl mx-auto">
            <h3 className="text-xl font-bold text-gray-900 mb-6">お探しの情報へジャンプ</h3>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <button 
                onClick={() => scrollToSection('comparison')}
                className="bg-white hover:bg-red-50 text-gray-900 font-semibold py-4 px-4 rounded-xl shadow-md hover:shadow-lg transition-all duration-300 cursor-pointer flex flex-col items-center"
              >
                <i className="ri-table-line text-3xl text-red-600 mb-2"></i>
                <span className="text-sm">店舗比較表</span>
              </button>
              
              <button 
                onClick={() => scrollToSection('wait-time')}
                className="bg-white hover:bg-red-50 text-gray-900 font-semibold py-4 px-4 rounded-xl shadow-md hover:shadow-lg transition-all duration-300 cursor-pointer flex flex-col items-center"
              >
                <i className="ri-time-line text-3xl text-red-600 mb-2"></i>
                <span className="text-sm">待ち時間</span>
              </button>
              
              <button 
                onClick={() => scrollToSection('stores')}
                className="bg-white hover:bg-red-50 text-gray-900 font-semibold py-4 px-4 rounded-xl shadow-md hover:shadow-lg transition-all duration-300 cursor-pointer flex flex-col items-center"
              >
                <i className="ri-store-line text-3xl text-red-600 mb-2"></i>
                <span className="text-sm">店舗詳細</span>
              </button>
              
              <a 
                href="https://sywgckeu.gensparkspace.com/"
                target="_blank"
                rel="noopener noreferrer"
                className="bg-white hover:bg-red-50 text-gray-900 font-semibold py-4 px-4 rounded-xl shadow-md hover:shadow-lg transition-all duration-300 cursor-pointer flex flex-col items-center"
              >
                <i className="ri-gamepad-line text-3xl text-red-600 mb-2"></i>
                <span className="text-sm">ゲームに挑戦</span>
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* 店舗比較表 */}
      <section id="comparison" className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">店舗比較表</h2>
            <p className="text-lg text-gray-600">各店舗の特徴を一目で比較できます</p>
          </div>

          <div className="bg-white rounded-2xl shadow-xl overflow-hidden border border-gray-200">
            <div className="overflow-x-auto">
              <table className="w-full text-sm md:text-base">
                <thead className="bg-red-600 text-white">
                  <tr>
                    <th className="px-4 py-4 text-left font-semibold whitespace-nowrap">店舗名</th>
                    <th className="px-4 py-4 text-center font-semibold whitespace-nowrap">店内飲食</th>
                    <th className="px-4 py-4 text-center font-semibold whitespace-nowrap">テイクアウト</th>
                    <th className="px-4 py-4 text-left font-semibold whitespace-nowrap text-lg">特徴</th>
                    <th className="px-4 py-4 text-left font-semibold whitespace-nowrap text-lg">営業日</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  <tr className="hover:bg-gray-50">
                    <td className="px-4 py-4 font-semibold text-gray-900 whitespace-nowrap">川豊本店</td>
                    <td className="px-4 py-4 text-center"><span className="text-green-600 text-xl">○</span></td>
                    <td className="px-4 py-4 text-center">
                      <div className="flex flex-col items-center">
                        <span className="text-green-600 text-xl">○</span>
                        <span className="text-xs text-gray-600 mt-1">（2軒上店で販売の日あり）</span>
                      </div>
                    </td>
                    <td className="px-4 py-4 text-gray-700 text-base">本店・文化財</td>
                    <td className="px-4 py-4 text-gray-700 text-base">年中無休</td>
                  </tr>
                  <tr className="hover:bg-gray-50">
                    <td className="px-4 py-4 font-semibold text-gray-900 whitespace-nowrap">川豊別館</td>
                    <td className="px-4 py-4 text-center"><span className="text-green-600 text-xl">○</span></td>
                    <td className="px-4 py-4 text-center"><span className="text-green-600 text-xl">○</span></td>
                    <td className="px-4 py-4 text-gray-700 text-base">日本庭園</td>
                    <td className="px-4 py-4 text-gray-700 text-base">年中無休</td>
                  </tr>
                  <tr className="hover:bg-gray-50">
                    <td className="px-4 py-4 font-semibold text-gray-900 whitespace-nowrap">川豊臨時店舗</td>
                    <td className="px-4 py-4 text-center"><span className="text-green-600 text-xl">○</span></td>
                    <td className="px-4 py-4 text-center"><span className="text-green-600 text-xl">○</span></td>
                    <td className="px-4 py-4 text-gray-700 text-base">700席・回転早</td>
                    <td className="px-4 py-4 text-gray-700 text-base">12/30～1/4、1/10～12<br />および1月の土日</td>
                  </tr>
                  <tr className="hover:bg-gray-50">
                    <td className="px-4 py-4 font-semibold text-gray-900 whitespace-nowrap">成田空港店</td>
                    <td className="px-4 py-4 text-center"><span className="text-green-600 text-xl">○</span></td>
                    <td className="px-4 py-4 text-center"><span className="text-green-600 text-xl">○</span></td>
                    <td className="px-4 py-4 text-gray-700 text-base">空港内</td>
                    <td className="px-4 py-4 text-gray-700 text-base">年中無休</td>
                  </tr>
                  <tr className="hover:bg-gray-50">
                    <td className="px-4 py-4 font-semibold text-gray-900 whitespace-nowrap">酒々井店</td>
                    <td className="px-4 py-4 text-center"><span className="text-green-600 text-xl">○</span></td>
                    <td className="px-4 py-4 text-center"><span className="text-green-600 text-xl">○</span></td>
                    <td className="px-4 py-4 text-gray-700 text-base">アウトレット</td>
                    <td className="px-4 py-4 text-gray-700 text-base">年中無休</td>
                  </tr>
                  <tr className="hover:bg-gray-50">
                    <td className="px-4 py-4 font-semibold text-gray-900 whitespace-nowrap">本店2軒上</td>
                    <td className="px-4 py-4 text-center"><span className="text-gray-400 text-xl">×</span></td>
                    <td className="px-4 py-4 text-center">
                      <div className="flex flex-col items-center">
                        <span className="text-green-600 text-2xl">◎</span>
                        <span className="text-xs text-gray-600 mt-1">お土産専用</span>
                      </div>
                    </td>
                    <td className="px-4 py-4 text-gray-700 text-base">お土産専用</td>
                    <td className="px-4 py-4 text-gray-700 text-base">本店混雑時<br />（1月のみ）</td>
                  </tr>
                  <tr className="hover:bg-gray-50">
                    <td className="px-4 py-4 font-semibold text-gray-900 whitespace-nowrap">JR成田駅前</td>
                    <td className="px-4 py-4 text-center"><span className="text-gray-400 text-xl">×</span></td>
                    <td className="px-4 py-4 text-center">
                      <div className="flex flex-col items-center">
                        <span className="text-green-600 text-2xl">◎</span>
                        <span className="text-xs text-gray-600 mt-1">お土産専用</span>
                      </div>
                    </td>
                    <td className="px-4 py-4 text-gray-700 text-base">駅近</td>
                    <td className="px-4 py-4 text-gray-700 text-base">12/30～1/4、1/10～12<br />および1月の土日</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </section>

      {/* 待ち時間確認 */}
      <section id="wait-time" className="py-16 bg-gradient-to-br from-gray-50 to-gray-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">現在の待ち時間</h2>
            <p className="text-lg text-gray-600 mb-2">最終更新: {currentTime.toLocaleTimeString('ja-JP', { hour: '2-digit', minute: '2-digit' })}</p>
            <p className="text-sm text-gray-500">※待ち時間は目安です。実際の状況により変動する場合があります。</p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
            {/* 川豊本店 */}
            <div className="bg-gradient-to-br from-red-50 to-orange-50 rounded-2xl shadow-xl p-6 md:p-8">
              <div className="flex items-center justify-between mb-6">
                <h3 className="text-2xl font-bold text-gray-900">川豊本店</h3>
                <i className="ri-store-line text-red-600 text-3xl"></i>
              </div>
              
              <div className="flex flex-col items-center justify-center py-8">
                <a 
                  href="http://unagi-kawatoyo.com/waitingtime"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex flex-col items-center gap-4 bg-white hover:bg-red-50 text-gray-900 font-bold py-8 px-12 rounded-2xl shadow-lg hover:shadow-2xl transition-all duration-300 cursor-pointer border-2 border-red-200 hover:border-red-400 w-full"
                >
                  <i className="ri-time-line text-6xl text-red-600"></i>
                  <div className="text-center">
                    <p className="text-xl md:text-2xl font-bold text-gray-900 mb-2">
                      川豊本店の待ち時間・<br />現在のお呼出番号は<br />こちらから確認してください
                    </p>
                    <div className="flex items-center justify-center gap-2 text-red-600 mt-4">
                      <span className="text-lg">詳細を見る</span>
                      <i className="ri-external-link-line text-2xl"></i>
                    </div>
                  </div>
                </a>
              </div>
            </div>

            {/* 川豊別館 */}
            <div className="bg-gradient-to-br from-blue-50 via-cyan-50 to-indigo-50 rounded-2xl shadow-xl p-6 md:p-8">
              <div className="flex items-center justify-between mb-6">
                <h3 className="text-2xl font-bold text-gray-900">川豊別館</h3>
                <i className="ri-store-2-line text-blue-600 text-3xl"></i>
              </div>
              
              <div className="space-y-4">
                <div className="bg-white rounded-xl p-4 shadow">
                  <p className="text-sm text-gray-600 mb-1">待ち組数</p>
                  {isLoading ? (
                    <p className="text-4xl font-bold text-blue-600">
                      <span className="inline-block animate-pulse">読み込み中...</span>
                    </p>
                  ) : waitTimeData ? (
                    <p className="text-4xl font-bold text-blue-600">
                      {waitTimeData.BekkancurrentWaiting}<span className="text-xl ml-1">組</span>
                    </p>
                  ) : (
                    <p className="text-4xl font-bold text-gray-400">
                      データなし
                    </p>
                  )}
                </div>
                
                <div className="bg-white rounded-xl p-4 shadow">
                  <p className="text-sm text-gray-600 mb-1">予想待ち時間</p>
                  {isLoading ? (
                    <p className="text-4xl font-bold text-cyan-600">
                      <span className="inline-block animate-pulse">読み込み中...</span>
                    </p>
                  ) : waitTimeData ? (
                    <p className="text-4xl font-bold text-cyan-600">
                      {waitTimeData.BekkancurrentWaiting * 2}<span className="text-xl ml-1">分</span>
                    </p>
                  ) : (
                    <p className="text-4xl font-bold text-gray-400">
                      データなし
                    </p>
                  )}
                </div>
                
                <div className="bg-white rounded-xl p-4 shadow">
                  <p className="text-sm text-gray-600 mb-1">現在のお呼出番号</p>
                  {isLoading ? (
                    <p className="text-4xl font-bold text-indigo-600">
                      <span className="inline-block animate-pulse">読み込み中...</span>
                    </p>
                  ) : waitTimeData ? (
                    <p className="text-4xl font-bold text-indigo-600">
                      {waitTimeData.BekkancurrentNumber}<span className="text-xl ml-1">番</span>
                    </p>
                  ) : (
                    <p className="text-4xl font-bold text-gray-400">
                      データなし
                    </p>
                  )}
                </div>
              </div>
            </div>

            {/* 川豊酒々井店 */}
            <div className="bg-gradient-to-br from-green-50 via-emerald-50 to-teal-50 rounded-2xl shadow-xl p-6 md:p-8">
              <div className="flex items-center justify-between mb-6">
                <h3 className="text-2xl font-bold text-gray-900">川豊酒々井店</h3>
                <i className="ri-store-3-line text-green-600 text-3xl"></i>
              </div>
              
              <div className="space-y-4">
                <div className="bg-white rounded-xl p-4 shadow">
                  <p className="text-sm text-gray-600 mb-1">待ち組数</p>
                  {isLoading ? (
                    <p className="text-4xl font-bold text-green-600">
                      <span className="inline-block animate-pulse">読み込み中...</span>
                    </p>
                  ) : waitTimeData ? (
                    <p className="text-4xl font-bold text-green-600">
                      {waitTimeData.ShisuicurrentWaiting}<span className="text-xl ml-1">組</span>
                    </p>
                  ) : (
                    <p className="text-4xl font-bold text-gray-400">
                      データなし
                    </p>
                  )}
                </div>
                
                <div className="bg-white rounded-xl p-4 shadow">
                  <p className="text-sm text-gray-600 mb-1">予想待ち時間</p>
                  {isLoading ? (
                    <p className="text-4xl font-bold text-emerald-600">
                      <span className="inline-block animate-pulse">読み込み中...</span>
                    </p>
                  ) : waitTimeData ? (
                    <p className="text-4xl font-bold text-emerald-600">
                      {Math.round(waitTimeData.ShisuicurrentWaiting * 1.5)}<span className="text-xl ml-1">分</span>
                    </p>
                  ) : (
                    <p className="text-4xl font-bold text-gray-400">
                      データなし
                    </p>
                  )}
                </div>
                
                <div className="bg-white rounded-xl p-4 shadow">
                  <p className="text-sm text-gray-600 mb-1">現在のお呼出番号</p>
                  {isLoading ? (
                    <p className="text-4xl font-bold text-teal-600">
                      <span className="inline-block animate-pulse">読み込み中...</span>
                    </p>
                  ) : waitTimeData ? (
                    <p className="text-4xl font-bold text-teal-600">
                      {waitTimeData.ShisuicurrentNumber}<span className="text-xl ml-1">番</span>
                    </p>
                  ) : (
                    <p className="text-4xl font-bold text-gray-400">
                      データなし
                    </p>
                  )}
                </div>
              </div>
            </div>

            {/* 川豊臨時店舗 */}
            <div className="bg-gradient-to-br from-purple-50 via-pink-50 to-rose-50 rounded-2xl shadow-xl p-6 md:p-8">
              <div className="flex items-center justify-between mb-6">
                <h3 className="text-2xl font-bold text-gray-900">川豊臨時店舗</h3>
                <i className="ri-shopping-bag-line text-purple-600 text-3xl"></i>
              </div>
              
              <div className="space-y-4">
                <div className="bg-white rounded-xl p-4 shadow">
                  <p className="text-sm text-gray-600 mb-1">予想待ち時間</p>
                  {isLoading ? (
                    <p className="text-4xl font-bold text-pink-600">
                      <span className="inline-block animate-pulse">読み込み中...</span>
                    </p>
                  ) : waitTimeData ? (
                    <p className="text-4xl font-bold text-pink-600">
                      {waitTimeData.YataicurrentNumber}<span className="text-xl ml-1">分</span>
                    </p>
                  ) : (
                    <p className="text-4xl font-bold text-gray-400">
                      データなし
                    </p>
                  )}
                </div>
                
                <div className="bg-amber-50 rounded-lg p-4 border-l-4 border-amber-500">
                  <p className="text-sm text-amber-800">
                    <i className="ri-information-line mr-1"></i>
                    ご注文後、すぐに店内へ入れます
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* 注意書き */}
          <div className="bg-amber-50 border-l-4 border-amber-500 rounded-lg p-6 md:p-8">
            <div className="flex items-start">
              <i className="ri-alert-line text-amber-600 text-3xl mr-4 flex-shrink-0"></i>
              <div>
                <h4 className="font-bold text-amber-900 mb-3 text-lg md:text-xl">ご来店前にご確認ください</h4>
                <ul className="space-y-2 text-sm md:text-base text-amber-800">
                  <li className="flex items-start">
                    <span className="mr-2">※</span>
                    <span>待ち時間は目安です</span>
                  </li>
                  <li className="flex items-start">
                    <span className="mr-2">※</span>
                    <span>混雑状況により受付を早めに終了する場合があります</span>
                  </li>
                  <li className="flex items-start">
                    <span className="mr-2">※</span>
                    <span>店舗により営業日・提供内容が異なります</span>
                  </li>
                  <li className="flex items-start">
                    <span className="mr-2">※</span>
                    <span>データは自動更新されますが、実際の状況と異なる場合があります</span>
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* 店舗詳細 */}
      <section id="stores" className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">店舗詳細</h2>
            <p className="text-lg text-gray-600">各店舗の詳しい情報をご確認ください</p>
          </div>

          {/* 店舗クイックリンク */}
          <div className="mb-12 bg-white rounded-2xl shadow-lg p-6">
            <h3 className="text-xl font-bold text-gray-900 mb-4 text-center">店舗を選択</h3>
            <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-3">
              <button 
                onClick={() => scrollToSection('honten')}
                className="bg-red-50 hover:bg-red-100 text-red-900 font-semibold py-3 px-3 rounded-lg transition-all duration-300 cursor-pointer text-sm whitespace-nowrap"
              >
                川豊本店
              </button>
              <button 
                onClick={() => scrollToSection('bekkan')}
                className="bg-red-50 hover:bg-red-100 text-red-900 font-semibold py-3 px-3 rounded-lg transition-all duration-300 cursor-pointer text-sm whitespace-nowrap"
              >
                川豊別館
              </button>
              <button 
                onClick={() => scrollToSection('rinji')}
                className="bg-red-50 hover:bg-red-100 text-red-900 font-semibold py-3 px-3 rounded-lg transition-all duration-300 cursor-pointer text-sm whitespace-nowrap"
              >
                川豊臨時店舗
              </button>
              <button 
                onClick={() => scrollToSection('airport')}
                className="bg-red-50 hover:bg-red-100 text-red-900 font-semibold py-3 px-3 rounded-lg transition-all duration-300 cursor-pointer text-sm whitespace-nowrap"
              >
                成田空港店
              </button>
              <button 
                onClick={() => scrollToSection('shisui')}
                className="bg-red-50 hover:bg-red-100 text-red-900 font-semibold py-3 px-3 rounded-lg transition-all duration-300 cursor-pointer text-sm whitespace-nowrap"
              >
                酒々井店
              </button>
              <button 
                onClick={() => scrollToSection('nikenjo')}
                className="bg-red-50 hover:bg-red-100 text-red-900 font-semibold py-3 px-3 rounded-lg transition-all duration-300 cursor-pointer text-sm whitespace-nowrap"
              >
                本店2軒上
              </button>
              <button 
                onClick={() => scrollToSection('ekimae')}
                className="bg-red-50 hover:bg-red-100 text-red-900 font-semibold py-3 px-3 rounded-lg transition-all duration-300 cursor-pointer text-sm whitespace-nowrap"
              >
                JR成田駅前
              </button>
            </div>
          </div>

          <div className="space-y-8">
            {/* 川豊本店 */}
            <div id="honten" className="bg-white rounded-2xl shadow-lg overflow-hidden scroll-mt-24">
              <div className="relative w-full h-96 md:h-[500px]">
                <img 
                  src="https://static.readdy.ai/image/a73d7a96b404061874ea59f844e08d9e/09613d8ec8b1488a8497aa24d0d39729.jpeg" 
                  alt="川豊本店"
                  className="w-full h-full object-cover"
                />
              </div>
              <div className="p-6 md:p-8">
                <h3 className="text-2xl md:text-3xl font-bold text-gray-900 mb-4">川豊 本店</h3>
                
                <div className="mb-6">
                  <div className="flex items-start mb-3">
                    <i className="ri-map-pin-line text-red-600 text-2xl mt-1 mr-3 flex-shrink-0"></i>
                    <div>
                      <p className="text-base md:text-lg text-gray-700 font-medium">千葉県成田市仲町386</p>
                      <a 
                        href="https://www.google.com/maps/search/?api=1&query=千葉県成田市仲町386" 
                        target="_blank" 
                        rel="noopener noreferrer"
                        className="inline-flex items-center text-blue-600 hover:text-blue-700 mt-2 text-sm md:text-base cursor-pointer"
                      >
                        <i className="ri-map-line mr-1"></i>
                        📍 アクセスを見る
                      </a>
                    </div>
                  </div>
                </div>

                <div className="space-y-4">
                  <div className="bg-amber-50 rounded-lg p-4 border-l-4 border-amber-600">
                    <h4 className="font-bold text-amber-900 mb-2 flex items-center text-base md:text-lg">
                      <i className="ri-time-line mr-2"></i>
                      営業時間
                    </h4>
                    <ul className="space-y-2 text-sm md:text-base text-amber-800">
                      <li className="flex items-start">
                        <span className="mr-2">•</span>
                        <span>毎日 10:00～17:00（ラストオーダー 17:00）</span>
                      </li>
                      <li className="flex items-start">
                        <span className="mr-2">•</span>
                        <span>年中無休</span>
                      </li>
                    </ul>
                  </div>

                  <div className="bg-red-50 rounded-lg p-4 border-l-4 border-red-600">
                    <h4 className="font-bold text-red-900 mb-2 flex items-center text-base md:text-lg">
                      <i className="ri-star-line mr-2"></i>
                      特徴
                    </h4>
                    <ul className="space-y-2 text-sm md:text-base text-red-800">
                      <li className="flex items-start">
                        <span className="mr-2">•</span>
                        <span>国の登録有形文化財でお食事ができます</span>
                      </li>
                      <li className="flex items-start">
                        <span className="mr-2">•</span>
                        <span>川豊の本店で最も人気の店舗</span>
                      </li>
                    </ul>
                  </div>

                  <div className="bg-blue-50 rounded-lg p-4 border-l-4 border-blue-600">
                    <h4 className="font-bold text-blue-900 mb-2 flex items-center text-base md:text-lg">
                      <i className="ri-information-line mr-2"></i>
                      ご来店方法
                    </h4>
                    <ul className="space-y-2 text-sm md:text-base text-blue-800">
                      <li className="flex items-start">
                        <span className="mr-2">•</span>
                        <span>店頭で整理券をお受け取りください</span>
                      </li>
                      <li className="flex items-start">
                        <span className="mr-2">•</span>
                        <span>整理券の番号を過ぎても無効になりません</span>
                      </li>
                      <li className="flex items-start">
                        <span className="mr-2">•</span>
                        <span>番号が過ぎた場合は店頭スタッフにお声がけください</span>
                      </li>
                      <li className="flex items-start">
                        <span className="mr-2">•</span>
                        <span>待ち時間が長い日は川豊臨時店舗を営業しております</span>
                      </li>
                    </ul>
                  </div>

                  <div className="bg-slate-50 rounded-lg p-4 border-l-4 border-slate-600">
                    <h4 className="font-bold text-slate-900 mb-2 flex items-center text-base md:text-lg">
                      <i className="ri-restaurant-line mr-2"></i>
                      お席のご案内
                    </h4>
                    <ul className="space-y-2 text-sm md:text-base text-slate-800">
                      <li className="flex items-start">
                        <span className="mr-2">•</span>
                        <span>1階席：テーブル席・掘りごたつ席</span>
                      </li>
                      <li className="flex items-start">
                        <span className="mr-2">•</span>
                        <span>2階席：座敷席</span>
                      </li>
                      <li className="flex items-start">
                        <span className="mr-2">•</span>
                        <span>ご案内時にお席のご希望をお聞きします</span>
                      </li>
                    </ul>
                  </div>
                </div>
              </div>
            </div>

            {/* 川豊別館 */}
            <div id="bekkan" className="bg-white rounded-2xl shadow-lg overflow-hidden scroll-mt-24">
              <div className="relative w-full h-96 md:h-[500px]">
                <img 
                  src="https://static.readdy.ai/image/a73d7a96b404061874ea59f844e08d9e/b8707a8936f7150a315db9ec10a646ed.jpeg" 
                  alt="川豊別館"
                  className="w-full h-full object-cover"
                />
              </div>
              <div className="p-6 md:p-8">
                <h3 className="text-2xl md:text-3xl font-bold text-gray-900 mb-4">川豊 別館</h3>
                
                <div className="mb-6">
                  <div className="flex items-start mb-3">
                    <i className="ri-map-pin-line text-red-600 text-2xl mt-1 mr-3 flex-shrink-0"></i>
                    <div>
                      <p className="text-base md:text-lg text-gray-700 font-medium">千葉県成田市東町113-1</p>
                      <p className="text-sm text-gray-600 mt-1">成田山新勝寺・JR成田駅から徒歩15分</p>
                      <a 
                        href="https://www.google.com/maps/search/?api=1&query=千葉県成田市東町113-1" 
                        target="_blank" 
                        rel="noopener noreferrer"
                        className="inline-flex items-center text-blue-600 hover:text-blue-700 mt-2 text-sm md:text-base cursor-pointer"
                      >
                        <i className="ri-map-line mr-1"></i>
                        📍 アクセスを見る
                      </a>
                    </div>
                  </div>
                </div>

                <div className="space-y-4">
                  <div className="bg-amber-50 rounded-lg p-4 border-l-4 border-amber-600">
                    <h4 className="font-bold text-amber-900 mb-2 flex items-center text-base md:text-lg">
                      <i className="ri-time-line mr-2"></i>
                      営業時間
                    </h4>
                    <div className="space-y-3 text-sm md:text-base text-amber-800">
                      <div className="bg-white rounded-lg p-3">
                        <p className="font-semibold text-amber-900 mb-1">平日（月～金）</p>
                        <p>11:00～14:30、16:30～20:30</p>
                      </div>
                      <div className="bg-white rounded-lg p-3">
                        <p className="font-semibold text-amber-900 mb-1">土日祝</p>
                        <p>11:00～15:00、16:30～20:30</p>
                      </div>
                    </div>
                  </div>

                  <div className="bg-red-50 rounded-lg p-4 border-l-4 border-red-600">
                    <h4 className="font-bold text-red-900 mb-2 flex items-center text-base md:text-lg">
                      <i className="ri-star-line mr-2"></i>
                      特徴
                    </h4>
                    <ul className="space-y-2 text-sm md:text-base text-red-800">
                      <li className="flex items-start">
                        <span className="mr-2">•</span>
                        <span>日本庭園を眺めながらお食事が可能</span>
                      </li>
                      <li className="flex items-start">
                        <span className="mr-2">•</span>
                        <span>和食・天ぷら・お刺身あり</span>
                      </li>
                      <li className="flex items-start">
                        <span className="mr-2">•</span>
                        <span>現在、御膳メニューは内容を縮小して営業中</span>
                      </li>
                    </ul>
                  </div>

                  <div className="bg-blue-50 rounded-lg p-4 border-l-4 border-blue-600">
                    <h4 className="font-bold text-blue-900 mb-2 flex items-center text-base md:text-lg">
                      <i className="ri-information-line mr-2"></i>
                      ご来店方法
                    </h4>
                    <ul className="space-y-2 text-sm md:text-base text-blue-800">
                      <li className="flex items-start">
                        <span className="mr-2">•</span>
                        <span>店頭の案内機で受付を行い、案内されたお席にお座りください</span>
                      </li>
                      <li className="flex items-start">
                        <span className="mr-2">•</span>
                        <span>整理券発券あり</span>
                      </li>
                    </ul>
                  </div>

                  <div className="bg-green-50 rounded-lg p-4 border-l-4 border-green-600">
                    <h4 className="font-bold text-green-900 mb-2 flex items-center text-base md:text-lg">
                      <i className="ri-restaurant-line mr-2"></i>
                      営業形態
                    </h4>
                    <p className="text-sm md:text-base text-green-800">店内飲食・テイクアウト</p>
                  </div>

                  <div className="bg-purple-50 rounded-lg p-4 border-l-4 border-purple-600">
                    <h4 className="font-bold text-purple-900 mb-2 flex items-center text-base md:text-lg">
                      <i className="ri-bank-card-line mr-2"></i>
                      お支払い方法
                    </h4>
                    <p className="text-sm md:text-base text-purple-800">現金・PayPay・クレジットカード等、多様な決済手段が使えます</p>
                  </div>
                </div>
              </div>
            </div>

            {/* 川豊臨時店舗 */}
            <div id="rinji" className="bg-white rounded-2xl shadow-lg overflow-hidden scroll-mt-24">
              <div className="relative w-full h-96 md:h-[500px]">
                <img 
                  src="https://static.readdy.ai/image/a73d7a96b404061874ea59f844e08d9e/f1888144c9a771c08fea2d30ad2f5155.jpeg" 
                  alt="川豊臨時店舗"
                  className="w-full h-full object-cover"
                />
              </div>
              <div className="p-6 md:p-8">
                <h3 className="text-2xl md:text-3xl font-bold text-gray-900 mb-4">川豊 臨時店舗（海老屋建物内）</h3>
                
                <div className="mb-6">
                  <div className="flex items-start mb-3">
                    <i className="ri-map-pin-line text-red-600 text-2xl mt-1 mr-3 flex-shrink-0"></i>
                    <div>
                      <p className="text-base md:text-lg text-gray-700 font-medium mb-2">千葉県成田市本町335番地</p>
                      <div className="space-y-1 text-sm text-gray-600">
                        <p>• 成田山新勝寺総門から徒歩1分</p>
                        <p>• 川豊本店から徒歩5分</p>
                      </div>
                      <a 
                        href="https://www.google.com/maps/search/?api=1&query=千葉県成田市本町335番地" 
                        target="_blank" 
                        rel="noopener noreferrer"
                        className="inline-flex items-center text-blue-600 hover:text-blue-700 mt-2 text-sm md:text-base cursor-pointer"
                      >
                        <i className="ri-map-line mr-1"></i>
                        📍 アクセスを見る
                      </a>
                    </div>
                  </div>
                </div>

                <div className="space-y-4">
                  <div className="bg-amber-50 rounded-lg p-4 border-l-4 border-amber-600">
                    <h4 className="font-bold text-amber-900 mb-2 flex items-center text-base md:text-lg">
                      <i className="ri-time-line mr-2"></i>
                      営業時間
                    </h4>
                    <p className="text-sm md:text-base text-amber-800">不定期（川豊本店の混雑時に営業）</p>
                  </div>

                  <div className="bg-green-50 rounded-lg p-4 border-l-4 border-green-600">
                    <h4 className="font-bold text-green-900 mb-2 flex items-center text-base md:text-lg">
                      <i className="ri-shopping-bag-line mr-2"></i>
                      販売メニュー（縮小メニュー）
                    </h4>
                    <ul className="space-y-2 text-sm md:text-base text-green-800">
                      <li className="flex items-start">
                        <span className="mr-2">•</span>
                        <span>うな重（うなぎ2/3尾）</span>
                      </li>
                      <li className="flex items-start">
                        <span className="mr-2">•</span>
                        <span>上うな重（うなぎ1尾）</span>
                      </li>
                      <li className="flex items-start">
                        <span className="mr-2">•</span>
                        <span>蒲焼（2/3尾）</span>
                      </li>
                      <li className="flex items-start">
                        <span className="mr-2">•</span>
                        <span>上蒲焼（1尾）</span>
                      </li>
                      <li className="flex items-start">
                        <span className="mr-2">•</span>
                        <span>骨煎餅</span>
                      </li>
                    </ul>
                  </div>

                  <div className="bg-orange-50 rounded-lg p-4 border-l-4 border-orange-600">
                    <h4 className="font-bold text-orange-900 mb-2 flex items-center text-base md:text-lg">
                      <i className="ri-restaurant-line mr-2"></i>
                      販売メニュー（縮小メニュー）
                    </h4>
                    <ul className="space-y-2 text-sm md:text-base text-orange-800">
                      <li className="flex items-start">
                        <span className="mr-2">•</span>
                        <span>うな重（うなぎ2/3尾）</span>
                      </li>
                      <li className="flex items-start">
                        <span className="mr-2">•</span>
                        <span>上うな重（うなぎ1尾）</span>
                      </li>
                      <li className="flex items-start">
                        <span className="mr-2">•</span>
                        <span>蒲焼（2/3尾）</span>
                      </li>
                      <li className="flex items-start">
                        <span className="mr-2">•</span>
                        <span>上蒲焼（1尾）</span>
                      </li>
                      <li className="flex items-start">
                        <span className="mr-2">•</span>
                        <span>骨煎餅</span>
                      </li>
                    </ul>
                  </div>

                  <div className="bg-blue-50 rounded-lg p-4 border-l-4 border-blue-600">
                    <h4 className="font-bold text-blue-900 mb-2 flex items-center text-base md:text-lg">
                      <i className="ri-goblet-line mr-2"></i>
                      アルコール販売
                    </h4>
                    <p className="text-sm md:text-base text-blue-800">アルコールは店内の自販機で販売しております</p>
                  </div>
                </div>
              </div>
            </div>

            {/* 成田空港店 */}
            <div id="airport" className="bg-white rounded-2xl shadow-lg overflow-hidden scroll-mt-24">
              <div className="relative w-full h-96 md:h-[500px]">
                <img 
                  src="https://static.readdy.ai/image/a73d7a96b404061874ea59f844e08d9e/0355e56cdb564cd30b1511ff0354a294.jpeg" 
                  alt="川豊成田空港店"
                  className="w-full h-full object-cover"
                />
              </div>
              <div className="p-6 md:p-8">
                <h3 className="text-2xl md:text-3xl font-bold text-gray-900 mb-4">川豊 成田空港店</h3>
                
                <div className="mb-6">
                  <div className="flex items-start mb-3">
                    <i className="ri-map-pin-line text-red-600 text-2xl mt-1 mr-3 flex-shrink-0"></i>
                    <div>
                      <p className="text-base md:text-lg text-gray-700 font-medium mb-2">成田国際空港 第1ターミナル 中央ビル新館4階</p>
                      <p className="text-sm text-gray-600">「NARITA Dining Terrace」（出国手続き前エリア）</p>
                      <a 
                        href="https://www.google.com/maps/search/?api=1&query=成田国際空港+第1ターミナル+中央ビル+4階" 
                        target="_blank" 
                        rel="noopener noreferrer"
                        className="inline-flex items-center text-blue-600 hover:text-blue-700 mt-2 text-sm md:text-base cursor-pointer"
                      >
                        <i className="ri-map-line mr-1"></i>
                        📍 アクセスを見る
                      </a>
                    </div>
                  </div>
                </div>

                <div className="space-y-4">
                  <div className="bg-red-50 rounded-lg p-4 border-l-4 border-red-600">
                    <h4 className="font-bold text-red-900 mb-2 flex items-center text-base md:text-lg">
                      <i className="ri-star-line mr-2"></i>
                      特徴
                    </h4>
                    <p className="text-sm md:text-base text-red-800">空港でも焼きたてのうなぎが食べて頂けます</p>
                  </div>

                  <div className="bg-amber-50 rounded-lg p-4 border-l-4 border-amber-600">
                    <h4 className="font-bold text-amber-900 mb-2 flex items-center text-base md:text-lg">
                      <i className="ri-time-line mr-2"></i>
                      営業時間
                    </h4>
                    <ul className="space-y-2 text-sm md:text-base text-amber-800">
                      <li className="flex items-start">
                        <span className="mr-2">•</span>
                        <span>年中無休</span>
                      </li>
                      <li className="flex items-start">
                        <span className="mr-2">•</span>
                        <span>8:00～20:30（ラストオーダー）</span>
                      </li>
                    </ul>
                  </div>

                  <div className="bg-blue-50 rounded-lg p-4 border-l-4 border-blue-600">
                    <h4 className="font-bold text-blue-900 mb-2 flex items-center text-base md:text-lg">
                      <i className="ri-information-line mr-2"></i>
                      ご来店方法
                    </h4>
                    <ul className="space-y-2 text-sm md:text-base text-blue-800">
                      <li className="flex items-start">
                        <span className="mr-2">•</span>
                        <span>整理券の発券はありません</span>
                      </li>
                      <li className="flex items-start">
                        <span className="mr-2">•</span>
                        <span>直接お越しください</span>
                      </li>
                    </ul>
                  </div>

                  <div className="bg-green-50 rounded-lg p-4 border-l-4 border-green-600">
                    <h4 className="font-bold text-green-900 mb-2 flex items-center text-base md:text-lg">
                      <i className="ri-restaurant-line mr-2"></i>
                      営業形態
                    </h4>
                    <p className="text-sm md:text-base text-green-800">店内飲食・テイクアウト</p>
                  </div>

                  <div className="bg-purple-50 rounded-lg p-4 border-l-4 border-purple-600">
                    <h4 className="font-bold text-purple-900 mb-2 flex items-center text-base md:text-lg">
                      <i className="ri-bank-card-line mr-2"></i>
                      お支払い方法
                    </h4>
                    <p className="text-sm md:text-base text-purple-800">現金・PayPay・クレジットカード等、多様な決済手段が使えます</p>
                  </div>
                </div>
              </div>
            </div>

            {/* 酒々井店 */}
            <div id="shisui" className="bg-white rounded-2xl shadow-lg overflow-hidden scroll-mt-24">
              <div className="relative w-full h-96 md:h-[500px]">
                <img 
                  src="https://static.readdy.ai/image/a73d7a96b404061874ea59f844e08d9e/cd9169df735294d4e47a66ee3f40f757.jpeg" 
                  alt="川豊酒々井プレミアムアウトレット店"
                  className="w-full h-full object-cover"
                />
              </div>
              <div className="p-6 md:p-8">
                <h3 className="text-2xl md:text-3xl font-bold text-gray-900 mb-4">川豊 酒々井プレミアムアウトレット店</h3>
                
                <div className="mb-6">
                  <div className="flex items-start mb-3">
                    <i className="ri-map-pin-line text-red-600 text-2xl mt-1 mr-3 flex-shrink-0"></i>
                    <div>
                      <p className="text-base md:text-lg text-gray-700 font-medium">酒々井プレミアムアウトレット レストランエリア</p>
                      <a 
                        href="https://www.google.com/maps/search/?api=1&query=酒々井プレミアムアウトレット" 
                        target="_blank" 
                        rel="noopener noreferrer"
                        className="inline-flex items-center text-blue-600 hover:text-blue-700 mt-2 text-sm md:text-base cursor-pointer"
                      >
                        <i className="ri-map-line mr-1"></i>
                        📍 アクセスを見る
                      </a>
                    </div>
                  </div>
                </div>

                <div className="space-y-4">
                  <div className="bg-amber-50 rounded-lg p-4 border-l-4 border-amber-600">
                    <h4 className="font-bold text-amber-900 mb-2 flex items-center text-base md:text-lg">
                      <i className="ri-time-line mr-2"></i>
                      営業時間
                    </h4>
                    <ul className="space-y-2 text-sm md:text-base text-amber-800">
                      <li className="flex items-start">
                        <span className="mr-2">•</span>
                        <span>毎日 11:00～21:00（ラストオーダー 20:30）</span>
                      </li>
                      <li className="flex items-start">
                        <span className="mr-2">•</span>
                        <span>年中無休</span>
                      </li>
                    </ul>
                  </div>

                  <div className="bg-red-50 rounded-lg p-4 border-l-4 border-red-600">
                    <h4 className="font-bold text-red-900 mb-2 flex items-center text-base md:text-lg">
                      <i className="ri-star-line mr-2"></i>
                      特徴
                    </h4>
                    <p className="text-sm md:text-base text-red-800">ショッピングを楽しみながら、川豊のうなぎも楽しんでいただけます</p>
                  </div>

                  <div className="bg-blue-50 rounded-lg p-4 border-l-4 border-blue-600">
                    <h4 className="font-bold text-blue-900 mb-2 flex items-center text-base md:text-lg">
                      <i className="ri-information-line mr-2"></i>
                      ご来店方法
                    </h4>
                    <ul className="space-y-2 text-sm md:text-base text-blue-800">
                      <li className="flex items-start">
                        <span className="mr-2">•</span>
                        <span>店頭の案内機で受付を行い、案内されたお席にお座りください</span>
                      </li>
                      <li className="flex items-start">
                        <span className="mr-2">•</span>
                        <span>整理券発券あり</span>
                      </li>
                    </ul>
                  </div>

                  <div className="bg-green-50 rounded-lg p-4 border-l-4 border-green-600">
                    <h4 className="font-bold text-green-900 mb-2 flex items-center text-base md:text-lg">
                      <i className="ri-restaurant-line mr-2"></i>
                      営業形態
                    </h4>
                    <p className="text-sm md:text-base text-green-800">店内飲食・テイクアウト</p>
                  </div>

                  <div className="bg-purple-50 rounded-lg p-4 border-l-4 border-purple-600">
                    <h4 className="font-bold text-purple-900 mb-2 flex items-center text-base md:text-lg">
                      <i className="ri-bank-card-line mr-2"></i>
                      お支払い方法
                    </h4>
                    <p className="text-sm md:text-base text-purple-800">現金・クレジットカード</p>
                  </div>
                </div>
              </div>
            </div>

            {/* 本店2軒上 */}
            <div id="nikenjo" className="bg-white rounded-2xl shadow-lg overflow-hidden scroll-mt-24">
              <div className="relative w-full h-96 md:h-[500px]">
                <img 
                  src="https://static.readdy.ai/image/a73d7a96b404061874ea59f844e08d9e/93372184ad18d3a8f34c70294e557a1d.jpeg" 
                  alt="本店2軒上臨時お土産販売所"
                  className="w-full h-full object-cover"
                />
              </div>
              <div className="p-6 md:p-8">
                <h3 className="text-2xl md:text-3xl font-bold text-gray-900 mb-4">本店2軒上 臨時お土産販売所</h3>
                
                <div className="mb-6">
                  <div className="flex items-start mb-3">
                    <i className="ri-map-pin-line text-red-600 text-2xl mt-1 mr-3 flex-shrink-0"></i>
                    <div>
                      <p className="text-base md:text-lg text-gray-700 font-medium mb-2">千葉県成田市仲町387</p>
                      <p className="text-sm text-gray-600">川豊本店から徒歩1分</p>
                      <a 
                        href="https://www.google.com/maps/search/?api=1&query=千葉県成田市仲町387" 
                        target="_blank" 
                        rel="noopener noreferrer"
                        className="inline-flex items-center text-blue-600 hover:text-blue-700 mt-2 text-sm md:text-base cursor-pointer"
                      >
                        <i className="ri-map-line mr-1"></i>
                        📍 アクセスを見る
                      </a>
                    </div>
                  </div>
                </div>

                <div className="space-y-4">
                  <div className="bg-amber-50 rounded-lg p-4 border-l-4 border-amber-600">
                    <h4 className="font-bold text-amber-900 mb-2 flex items-center text-base md:text-lg">
                      <i className="ri-time-line mr-2"></i>
                      営業時間
                    </h4>
                    <p className="text-sm md:text-base text-amber-800">不定期（川豊本店の混雑時に営業、1月のみ）</p>
                  </div>

                  <div className="bg-orange-50 rounded-lg p-4 border-l-4 border-orange-600">
                    <h4 className="font-bold text-orange-900 mb-2 flex items-center text-base md:text-lg">
                      <i className="ri-shopping-bag-line mr-2"></i>
                      販売メニュー（縮小メニュー）
                    </h4>
                    <ul className="space-y-2 text-sm md:text-base text-orange-800">
                      <li className="flex items-start">
                        <span className="mr-2">•</span>
                        <span>うな重（うなぎ2/3尾）</span>
                      </li>
                      <li className="flex items-start">
                        <span className="mr-2">•</span>
                        <span>上うな重（うなぎ1尾）</span>
                      </li>
                      <li className="flex items-start">
                        <span className="mr-2">•</span>
                        <span>蒲焼（2/3尾）</span>
                      </li>
                      <li className="flex items-start">
                        <span className="mr-2">•</span>
                        <span>上蒲焼（1尾）</span>
                      </li>
                      <li className="flex items-start">
                        <span className="mr-2">•</span>
                        <span>骨煎餅</span>
                      </li>
                    </ul>
                  </div>

                  <div className="bg-red-50 rounded-lg p-4 border-l-4 border-red-600">
                    <h4 className="font-bold text-red-900 mb-2 flex items-center text-base md:text-lg">
                      <i className="ri-bank-card-line mr-2"></i>
                      時間指定受け取りについて
                    </h4>
                    <ul className="space-y-2 text-sm md:text-base text-red-800">
                      <li className="flex items-start">
                        <span className="mr-2">•</span>
                        <span>時間指定で商品を受け取りに来られる方は、お電話番号をいただいております</span>
                      </li>
                      <li className="flex items-start">
                        <span className="mr-2">•</span>
                        <span>万が一、臨時お土産販売所が閉まっていた場合、お引き渡しは川豊本店になります</span>
                      </li>
                    </ul>
                  </div>
                </div>
              </div>
            </div>

            {/* JR成田駅前 */}
            <div id="ekimae" className="bg-white rounded-2xl shadow-lg overflow-hidden scroll-mt-24">
              <div className="relative w-full h-96 md:h-[500px]">
                <img 
                  src="https://static.readdy.ai/image/a73d7a96b404061874ea59f844e08d9e/9815cd4670c9fa6e26ba5f3d53f43213.jpeg" 
                  alt="JR成田駅前臨時お土産販売所"
                  className="w-full h-full object-cover"
                />
              </div>
              <div className="p-6 md:p-8">
                <h3 className="text-2xl md:text-3xl font-bold text-gray-900 mb-4">JR成田駅前 臨時お土産販売所</h3>
                
                <div className="mb-6">
                  <div className="flex items-start mb-3">
                    <i className="ri-map-pin-line text-red-600 text-2xl mt-1 mr-3 flex-shrink-0"></i>
                    <div>
                      <p className="text-base md:text-lg text-gray-700 font-medium mb-2">JR成田駅東口 ニューデイズ様前</p>
                      <p className="text-sm text-red-600 font-semibold">🐱 まねきねこが目印です！</p>
                    </div>
                  </div>
                </div>

                <div className="space-y-4">
                  <div className="bg-green-50 rounded-lg p-4 border-l-4 border-green-600">
                    <h4 className="font-bold text-green-900 mb-2 flex items-center text-base md:text-lg">
                      <i className="ri-shopping-bag-line mr-2"></i>
                      内容
                    </h4>
                    <ul className="space-y-2 text-sm md:text-base text-green-800">
                      <li className="flex items-start">
                        <span className="mr-2">•</span>
                        <span>お土産販売のみ</span>
                      </li>
                    </ul>
                  </div>

                  <div className="bg-orange-50 rounded-lg p-4 border-l-4 border-orange-600">
                    <h4 className="font-bold text-orange-900 mb-2 flex items-center text-base md:text-lg">
                      <i className="ri-restaurant-line mr-2"></i>
                      販売メニュー
                    </h4>
                    <ul className="space-y-2 text-sm md:text-base text-orange-800">
                      <li className="flex items-start">
                        <span className="mr-2">•</span>
                        <span>うなぎ弁当（うなぎ2/3尾）</span>
                      </li>
                      <li className="flex items-start">
                        <span className="mr-2">•</span>
                        <span>上うなぎ弁当（うなぎ1尾）</span>
                      </li>
                    </ul>
                  </div>

                  <div className="bg-amber-50 rounded-lg p-4 border-l-4 border-amber-600">
                    <h4 className="font-bold text-amber-900 mb-2 flex items-center text-base md:text-lg">
                      <i className="ri-time-line mr-2"></i>
                      営業時間
                    </h4>
                    <div className="space-y-3 text-sm md:text-base text-amber-800">
                      <div className="bg-white rounded-lg p-3">
                        <p className="font-semibold text-amber-900 mb-1">1月1日～3日</p>
                        <p>9:00～18:00</p>
                      </div>
                      <div className="bg-white rounded-lg p-3">
                        <p className="font-semibold text-amber-900 mb-1">1月4日、10日、11日、12日</p>
                        <p>11:00～16:00</p>
                      </div>
                      <div className="bg-white rounded-lg p-3">
                        <p className="font-semibold text-amber-900 mb-1">その後の土日</p>
                        <p>11:30～15:00</p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ゲーム案内 */}
      <section className="py-16 bg-gradient-to-br from-gray-50 to-gray-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-gradient-to-br from-indigo-50 via-purple-50 to-pink-50 rounded-2xl shadow-xl p-6 md:p-8 border-2 border-purple-200">
            <div className="flex flex-col md:flex-row items-center gap-6">
              <div className="w-full md:w-1/3">
                <img 
                  src="https://static.readdy.ai/image/a73d7a96b404061874ea59f844e08d9e/fd0d1e4e2c2f1b1f059ed69b9495776d.jpeg" 
                  alt="川豊うなぎゲーム"
                  className="w-full h-auto rounded-xl shadow-lg"
                />
              </div>
              <div className="w-full md:w-2/3 text-center md:text-left">
                <div className="flex items-center justify-center md:justify-start mb-4">
                  <i className="ri-gamepad-line text-4xl text-purple-600 mr-3"></i>
                  <h3 className="text-2xl md:text-3xl font-bold text-gray-900">川豊 うなぎゲーム</h3>
                </div>
                <p className="text-base md:text-lg text-gray-700 mb-4 leading-relaxed">
                  お待ち時間にどうぞ！川→豊→う→な→ぎという文字をひたすら繋げていくゲームです。<br />
                  <span className="text-purple-600 font-semibold">当店スタッフの最高得点：15,515点</span>
                </p>
                <a 
                  href="https://sywgckeu.gensparkspace.com/"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center bg-purple-600 hover:bg-purple-700 text-white font-bold py-3 px-8 rounded-full shadow-lg hover:shadow-xl transition-all duration-300 cursor-pointer whitespace-nowrap"
                >
                  <i className="ri-gamepad-line text-2xl mr-2"></i>
                  ゲームに挑戦する
                  <i className="ri-arrow-right-line ml-2"></i>
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* フッター */}
      <footer className="bg-gray-900 text-white py-8">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <p className="text-lg font-semibold text-white mb-2">Unagi Kawatoyo</p>
            <p className="text-sm text-gray-400">© 2025 川豊グループ. All rights reserved.</p>
          </div>
        </div>
      </footer>
    </div>
  );
}